import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API } from "@/App";
import { 
  ArrowLeft, Calendar, Clock, User, Star, TrendingUp, 
  CheckCircle, XCircle, Phone, Mail, MapPin, Edit,
  ChevronRight, MessageSquare, Bell, Settings, LogOut,
  Briefcase, DollarSign, Eye, Users, Check, X
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Professional Auth Context
const ProAuthContext = createContext(null);

export const useProAuth = () => useContext(ProAuthContext);

// Auth fetch for professionals
const proAuthFetch = async (url, options = {}) => {
  const token = localStorage.getItem("pro_session_token");
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      "Authorization": `Bearer ${token}`
    },
    credentials: "include"
  });
};

// Login Component
function ProfessionalLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || phone.length !== 4) {
      toast.error("Inserisci email e ultime 4 cifre del telefono");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/professional/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, phone })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("pro_session_token", data.session_token);
        toast.success("Benvenuto!");
        onLogin(data.professional);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel login");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Briefcase className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Portale Professionisti
          </h1>
          <p className="text-[#86868B] mt-2">Accedi alla tua dashboard Zampa</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="La tua email registrata"
                className="h-12 rounded-xl pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
              Codice (ultime 4 cifre telefono)
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                maxLength={4}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                placeholder="1234"
                className="h-12 rounded-xl pl-10 text-center text-2xl tracking-widest"
              />
            </div>
            <p className="text-xs text-[#86868B] mt-1">Es: se il tuo numero è +39 333 123 4567, inserisci 4567</p>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full h-12 bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl text-lg font-semibold"
          >
            {loading ? "Accesso..." : "Accedi"}
          </Button>
        </form>

        <p className="text-center text-sm text-[#86868B] mt-6">
          Non hai un account?{" "}
          <a href="/professional/register" className="text-[#FF6B4A] font-medium">
            Registrati qui
          </a>
        </p>
      </div>
    </div>
  );
}

// Dashboard Component
function ProfessionalDashboardContent({ professional, onLogout }) {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview"); // overview, bookings, profile
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState(professional);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await proAuthFetch(`${API}/professional/dashboard`);
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
        setProfileData(data.professional);
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async (status = null) => {
    try {
      let url = `${API}/professional/bookings`;
      if (status) url += `?status=${status}`;
      const response = await proAuthFetch(url);
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento prenotazioni");
    }
  };

  const handleBookingAction = async (bookingId, status) => {
    try {
      const response = await proAuthFetch(`${API}/professional/bookings/${bookingId}?status=${status}`, {
        method: "PATCH"
      });
      if (response.ok) {
        toast.success(status === "confirmed" ? "Prenotazione confermata!" : "Prenotazione aggiornata");
        fetchDashboard();
        fetchBookings();
        setSelectedBooking(null);
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleProfileUpdate = async () => {
    try {
      const response = await proAuthFetch(`${API}/professional/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profileData)
      });
      if (response.ok) {
        toast.success("Profilo aggiornato!");
        setEditMode(false);
        fetchDashboard();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleLogout = async () => {
    try {
      await proAuthFetch(`${API}/professional/logout`, { method: "POST" });
      localStorage.removeItem("pro_session_token");
      onLogout();
    } catch (error) {
      localStorage.removeItem("pro_session_token");
      onLogout();
    }
  };

  useEffect(() => {
    if (activeTab === "bookings") {
      fetchBookings();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#FF6B4A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF6B4A] to-[#FF8F6B] text-white">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                <User size={28} />
              </div>
              <div>
                <h1 className="text-xl font-bold">{professional.name}</h1>
                <p className="text-white/80 text-sm flex items-center gap-2">
                  {professional.verified && <CheckCircle size={14} />}
                  {professional.verified ? "Certificato Zampa" : professional.profession_type}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-white hover:bg-white/20"
            >
              <LogOut size={20} />
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            {[
              { id: "overview", label: "Dashboard", icon: TrendingUp },
              { id: "bookings", label: "Prenotazioni", icon: Calendar },
              { id: "profile", label: "Profilo", icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                  activeTab === tab.id 
                    ? 'bg-white text-[#FF6B4A]' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl p-5 border border-black/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#86868B] text-sm">Richieste in attesa</span>
                  <Bell className="text-amber-500" size={20} />
                </div>
                <p className="text-3xl font-bold text-[#1D1D1F]">{stats.pending_requests || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-black/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#86868B] text-sm">Questo mese</span>
                  <Calendar className="text-blue-500" size={20} />
                </div>
                <p className="text-3xl font-bold text-[#1D1D1F]">{stats.bookings_this_month || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-black/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#86868B] text-sm">Valutazione</span>
                  <Star className="text-amber-400" size={20} />
                </div>
                <p className="text-3xl font-bold text-[#1D1D1F]">{stats.rating?.toFixed(1) || "N/A"}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-black/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#86868B] text-sm">Totale prenotazioni</span>
                  <Users className="text-emerald-500" size={20} />
                </div>
                <p className="text-3xl font-bold text-[#1D1D1F]">{stats.total_bookings || 0}</p>
              </div>
            </div>

            {/* Pending Bookings */}
            {dashboardData?.pending_bookings?.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-black/5">
                <h2 className="font-bold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
                  <Bell className="text-amber-500" size={20} />
                  Richieste in attesa ({dashboardData.pending_bookings.length})
                </h2>
                <div className="space-y-3">
                  {dashboardData.pending_bookings.map(booking => (
                    <div 
                      key={booking.booking_id}
                      className="flex items-center justify-between p-4 rounded-xl bg-amber-50 border border-amber-100"
                    >
                      <div>
                        <p className="font-semibold text-[#1D1D1F]">{booking.client_name || "Cliente"}</p>
                        <p className="text-sm text-[#86868B]">
                          {booking.service_type} • {new Date(booking.date).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm"
                          onClick={() => handleBookingAction(booking.booking_id, "confirmed")}
                          className="bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                        >
                          <Check size={16} />
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => handleBookingAction(booking.booking_id, "rejected")}
                          className="rounded-xl text-red-500 border-red-200"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Reviews */}
            {dashboardData?.recent_reviews?.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-black/5">
                <h2 className="font-bold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
                  <MessageSquare className="text-blue-500" size={20} />
                  Recensioni recenti
                </h2>
                <div className="space-y-4">
                  {dashboardData.recent_reviews.map(review => (
                    <div key={review.review_id} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex">
                          {[1,2,3,4,5].map(i => (
                            <Star 
                              key={i} 
                              size={14} 
                              className={i <= review.rating ? "text-amber-400 fill-amber-400" : "text-gray-200"} 
                            />
                          ))}
                        </div>
                        <span className="text-sm font-medium">{review.user_name}</span>
                      </div>
                      <p className="text-sm text-[#86868B]">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!dashboardData?.pending_bookings?.length && !dashboardData?.recent_reviews?.length && (
              <div className="bg-white rounded-2xl p-12 border border-black/5 text-center">
                <Calendar className="mx-auto text-gray-300 mb-4" size={48} />
                <h3 className="font-semibold text-[#1D1D1F] mb-2">Nessuna attività recente</h3>
                <p className="text-[#86868B]">Le tue prenotazioni e recensioni appariranno qui</p>
              </div>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="space-y-4">
            <div className="flex gap-2 mb-4">
              {["all", "pending", "confirmed", "completed"].map(status => (
                <Button
                  key={status}
                  variant={status === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => fetchBookings(status === "all" ? null : status)}
                  className="rounded-xl"
                >
                  {status === "all" ? "Tutte" : status === "pending" ? "In attesa" : status === "confirmed" ? "Confermate" : "Completate"}
                </Button>
              ))}
            </div>

            {bookings.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 border border-black/5 text-center">
                <Calendar className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-[#86868B]">Nessuna prenotazione trovata</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.map(booking => (
                  <div 
                    key={booking.booking_id}
                    className="bg-white rounded-2xl p-4 border border-black/5"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-[#1D1D1F]">
                          {booking.client?.name || booking.client_name || "Cliente"}
                        </p>
                        <p className="text-sm text-[#86868B]">
                          {booking.service_type} • {new Date(booking.date).toLocaleDateString('it-IT')}
                          {booking.pet && ` • ${booking.pet.name}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          booking.status === "pending" ? "bg-amber-100 text-amber-700" :
                          booking.status === "confirmed" ? "bg-emerald-100 text-emerald-700" :
                          booking.status === "completed" ? "bg-blue-100 text-blue-700" :
                          "bg-red-100 text-red-700"
                        }`}>
                          {booking.status === "pending" ? "In attesa" :
                           booking.status === "confirmed" ? "Confermata" :
                           booking.status === "completed" ? "Completata" : "Rifiutata"}
                        </span>
                        {booking.status === "pending" && (
                          <>
                            <Button 
                              size="sm"
                              onClick={() => handleBookingAction(booking.booking_id, "confirmed")}
                              className="bg-emerald-500 hover:bg-emerald-600 rounded-xl h-8"
                            >
                              Conferma
                            </Button>
                          </>
                        )}
                        {booking.status === "confirmed" && (
                          <Button 
                            size="sm"
                            onClick={() => handleBookingAction(booking.booking_id, "completed")}
                            className="bg-blue-500 hover:bg-blue-600 rounded-xl h-8"
                          >
                            Completata
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="bg-white rounded-2xl p-6 border border-black/5">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-bold text-lg text-[#1D1D1F]">Il tuo profilo</h2>
              <Button 
                variant={editMode ? "default" : "outline"}
                onClick={() => editMode ? handleProfileUpdate() : setEditMode(true)}
                className="rounded-xl"
              >
                {editMode ? "Salva" : <><Edit size={16} className="mr-2" /> Modifica</>}
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#86868B] mb-1">Nome attività</label>
                  {editMode ? (
                    <Input 
                      value={profileData.name || ""} 
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      className="rounded-xl"
                    />
                  ) : (
                    <p className="font-medium text-[#1D1D1F]">{profileData.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#86868B] mb-1">Città</label>
                  {editMode ? (
                    <Input 
                      value={profileData.city || ""} 
                      onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                      className="rounded-xl"
                    />
                  ) : (
                    <p className="font-medium text-[#1D1D1F]">{profileData.city}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#86868B] mb-1">Zona servita</label>
                {editMode ? (
                  <Input 
                    value={profileData.zone || ""} 
                    onChange={(e) => setProfileData({...profileData, zone: e.target.value})}
                    className="rounded-xl"
                  />
                ) : (
                  <p className="font-medium text-[#1D1D1F]">{profileData.zone || "-"}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#86868B] mb-1">Descrizione</label>
                {editMode ? (
                  <textarea 
                    value={profileData.description || ""} 
                    onChange={(e) => setProfileData({...profileData, description: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#FF6B4A] outline-none"
                    rows={3}
                  />
                ) : (
                  <p className="text-[#1D1D1F]">{profileData.description || "-"}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#86868B] mb-1">Telefono</label>
                  {editMode ? (
                    <Input 
                      value={profileData.phone || ""} 
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      className="rounded-xl"
                    />
                  ) : (
                    <p className="font-medium text-[#1D1D1F]">{profileData.phone}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#86868B] mb-1">Prezzo da</label>
                  {editMode ? (
                    <Input 
                      type="number"
                      value={profileData.price_from || ""} 
                      onChange={(e) => setProfileData({...profileData, price_from: parseFloat(e.target.value)})}
                      className="rounded-xl"
                    />
                  ) : (
                    <p className="font-medium text-[#1D1D1F]">{profileData.price_from ? `€${profileData.price_from}` : "-"}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#86868B] mb-1">Sito web</label>
                {editMode ? (
                  <Input 
                    value={profileData.website || ""} 
                    onChange={(e) => setProfileData({...profileData, website: e.target.value})}
                    className="rounded-xl"
                  />
                ) : (
                  <p className="font-medium text-[#1D1D1F]">{profileData.website || "-"}</p>
                )}
              </div>

              {/* Status Badge */}
              <div className="pt-4 border-t">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#86868B]">Stato account:</span>
                  {profileData.verified ? (
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                      <CheckCircle size={14} /> Certificato Zampa
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium">
                      <CheckCircle size={14} /> Approvato
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Main Export
export default function ProfessionalPortal() {
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    const token = localStorage.getItem("pro_session_token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await proAuthFetch(`${API}/professional/me`);
      if (response.ok) {
        const data = await response.json();
        setProfessional(data);
      } else {
        localStorage.removeItem("pro_session_token");
      }
    } catch (error) {
      localStorage.removeItem("pro_session_token");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#FF6B4A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!professional) {
    return <ProfessionalLogin onLogin={setProfessional} />;
  }

  return (
    <ProfessionalDashboardContent 
      professional={professional} 
      onLogout={() => setProfessional(null)} 
    />
  );
}
